import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { callApi, HttpMethod } from "src/services/apiService";
import { Helmet } from "react-helmet-async";
import styles from "./Index.module.css";

// Form validation schema
const signupSchema = z
  .object({
    firstName: z.string().min(1, "First name is required"),
    lastName: z.string().min(1, "Last name is required"),
    email: z.string().email("Please enter a valid email address"),
    phoneNumber: z
      .string()
      .min(10, "Phone number must be at least 10 digits")
      .max(15, "Phone number must be at most 15 digits"),
    company: z.string().optional(),
    // Street Address fields
    address: z.string().min(1, "Street address is required"),
    city: z.string().min(1, "City is required"),
    state: z.string().min(1, "State is required"),
    postalCode: z.string().min(1, "Postal code is required"),
    country: z.enum(["US", "Canada"], {
      error: "Country is required",
    }),
    // Credit Card fields
    cardNumber: z
      .string()
      .min(13, "Card number must be at least 13 digits")
      .max(19, "Card number must be at most 19 digits"),
    expiryMonth: z.string().min(1, "Expiry month is required"),
    expiryYear: z.string().min(1, "Expiry year is required"),
    cvv: z
      .string()
      .min(3, "CVV must be at least 3 digits")
      .max(4, "CVV must be at most 4 digits"),
    // Billing Address fields
    sameAsStreetAddress: z.boolean().optional(),
    billingAddress: z.string().optional(),
    billingCity: z.string().optional(),
    billingState: z.string().optional(),
    billingPostalCode: z.string().optional(),
    billingCountry: z.enum(["US", "Canada"]).optional(),
    username: z
      .string()
      .min(3, "Username must be at least 3 characters")
      .regex(
        /^[^'\\/:*?<>| ]+$/,
        "Username cannot contain spaces or the following characters: ' \\ / : * ? < > |",
      )
      .refine(
        (username) => !username.toLowerCase().endsWith(".sysadmin"),
        "Username cannot end with .sysadmin",
      ),
    password: z
      .string()
      .min(8, "Password must be at least 8 characters")
      .refine(
        (password) => /[A-Z]/.test(password),
        "Password must contain at least one uppercase letter",
      )
      .refine(
        (password) => /[a-z]/.test(password),
        "Password must contain at least one lowercase letter",
      )
      .refine(
        (password) => /\d/.test(password),
        "Password must contain at least one digit",
      )
      .refine(
        (password) => /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password),
        "Password must contain at least one special character",
      ),
    confirmPassword: z.string().min(8, "Please confirm your password"),
    agreeToCharges: z.boolean().refine((val) => val === true, {
      message: "You must agree to the annual subscription charges to continue",
    }),
    agreeToTerms: z.boolean().refine((val) => val === true, {
      message: "You must agree to the Services Agreement to continue",
    }),
  })
  .refine((data) => data.password === data.confirmPassword, {
    message: "Passwords don't match",
    path: ["confirmPassword"],
  })
  .refine(
    (data) => {
      // If same as street address is not checked, billing fields are required
      if (!data.sameAsStreetAddress) {
        return (
          data.billingAddress &&
          data.billingCity &&
          data.billingState &&
          data.billingPostalCode &&
          data.billingCountry
        );
      }
      return true;
    },
    {
      message:
        "Billing address fields are required when not same as street address",
      path: ["billingAddress"],
    },
  );

type SignupFormData = z.infer<typeof signupSchema>;

// Type for the data sent to the server (with converted expiry fields)
type ServerSignupData = Omit<SignupFormData, "expiryMonth" | "expiryYear"> & {
  expiryMonth: number;
  expiryYear: number;
};

const ComplianceGuideSignup: React.FC = () => {
  const navigate = useNavigate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<{
    type: "success" | "error";
    text: string;
  } | null>(null);
  const [sameAsStreetAddress, setSameAsStreetAddress] = useState(false);
  const [isSignupSuccess, setIsSignupSuccess] = useState(false);
  const [accountName, setAccountName] = useState<string>("");

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
    getValues,
    setValue,
  } = useForm<SignupFormData>({
    resolver: zodResolver(signupSchema),
  });

  const onSubmit = async (data: SignupFormData) => {
    setIsSubmitting(true);
    setSubmitMessage(null);

    try {
      // If same as street address is checked, copy street address data to billing fields
      const formData: ServerSignupData = {
        ...data,
        expiryMonth: parseInt(data.expiryMonth, 10),
        expiryYear: parseInt(data.expiryYear.slice(-2), 10),
      };

      if (data.sameAsStreetAddress) {
        formData.billingAddress = data.address;
        formData.billingCity = data.city;
        formData.billingState = data.state;
        formData.billingPostalCode = data.postalCode;
        formData.billingCountry = data.country;
      }
      const apiUrl = "complianceguidesignup";
      const response = await callApi(apiUrl, HttpMethod.POST, formData);
      if (response.success === true) {
        setIsSignupSuccess(true);
        setAccountName(response.acctId || "");
        setSubmitMessage({
          type: "success",
          text: "Account created successfully! You can now sign in to access the compliance guide.",
        });
      } else {
        setSubmitMessage({
          type: "error",
          text:
            response.message ||
            "There was an error submitting your request. Please try again.",
        });
      }
    } catch (error: any) {
      setSubmitMessage({
        type: "error",
        text:
          error?.data?.message ||
          "There was an error submitting your request. Please try again.",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <>
      <Helmet>
        <title>Sign Up - Compliance Guide</title>
        <meta
          name="description"
          content="Sign up for access to our comprehensive compliance guide and tools"
        />
      </Helmet>

      <div className={`container-fluid ${styles.signupContainer}`}>
        <div className="row justify-content-center">
          <div className="col">
            <div className={`card shadow ${styles.signupCard}`}>
              <div className={`card-header text-white ${styles.cardHeader}`}>
                <h2 className={`card-title mb-0 ${styles.cardTitle}`}>
                  <i className="bi bi-shield-check me-2"></i>
                  Sign Up for the Compliance Guide
                </h2>
                <p
                  className="mb-0 mt-2"
                  style={{ fontSize: "0.95rem", opacity: 0.95 }}
                >
                  Your Complete Guide to Direct Marketing Compliance
                </p>
              </div>
              <div className={`card-body ${styles.cardBody}`}>
                {isSignupSuccess ? (
                  // Success confirmation view
                  <div className="text-center py-4">
                    <div className="mb-4">
                      <i
                        className="bi bi-check-circle-fill text-success"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <h3 className="mb-3">Welcome to Compliance Guide!</h3>
                    <p className="text-muted mb-4">
                      Your account <strong>{accountName}</strong> has been
                      created successfully. You now have full access to all
                      compliance tools, regulatory updates, and expert
                      resources.
                    </p>
                    {/* <div className={`${styles.successBenefits} mb-4`}>
                      <p className="mb-2">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        Unlimited access to regulatory guides
                      </p>
                      <p className="mb-2">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        Real-time compliance updates
                      </p>
                      <p className="mb-2">
                        <i className="bi bi-check-circle text-success me-2"></i>
                        AI-powered compliance assistant
                      </p>
                    </div> */}
                    <button
                      type="button"
                      className={`btn btn-primary ${styles.btnPrimary}`}
                      onClick={() => navigate("/compliance-guide/intro")}
                    >
                      <i className="bi bi-arrow-right-circle me-2"></i>
                      Start Exploring Your Benefits
                    </button>
                  </div>
                ) : (
                  // Form view
                  <>
                    {/* Value Proposition Section */}
                    <div className={`${styles.valueProposition} mb-4`}>
                      <h4 className={styles.valueTitle}>
                        <i className="bi bi-check-lg text-success me-1"></i>
                        Everything You Need to Stay Compliant
                      </h4>
                      <div className="row g-3 mb-3">
                        <div className="col-md-6">
                          <div className={styles.benefitItem}>
                            <i className="bi bi-book text-primary me-2"></i>
                            <strong>
                              Comprehensive Regulatory Guides and Chart
                            </strong>
                            <p className="small text-muted mb-0">
                              Stay current with Direct Marketing, TCPA, Do Not
                              Call, and state-specific regulations
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className={styles.benefitItem}>
                            <i className="bi bi-graph-up-arrow text-success me-2"></i>
                            <strong>Real-Time Compliance Updates</strong>
                            <p className="small text-muted mb-0">
                              Never miss critical regulatory changes
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className={styles.benefitItem}>
                            <i className="bi bi-robot text-info me-2"></i>
                            <strong>AI Compliance Assistant</strong>
                            <p className="small text-muted mb-0">
                              Get instant answers to compliance questions 24/7
                            </p>
                          </div>
                        </div>
                        <div className="col-md-6">
                          <div className={styles.benefitItem}>
                            <i className="bi bi-geo-alt text-danger me-2"></i>
                            <strong>Interactive DNC Maps & Tools</strong>
                            <p className="small text-muted mb-0">
                              Visualize compliance requirements by jurisdiction
                            </p>
                          </div>
                        </div>
                      </div>

                      {/* Pricing Callout */}
                      <div className={`${styles.pricingCallout} mt-4`}>
                        <div className="d-flex align-items-center justify-content-between">
                          <div>
                            <h3 className="mb-0">
                              <span className={styles.priceAmount}>$1,200</span>
                              <span className={styles.pricePeriod}>/year</span>
                            </h3>
                            <p className="small mb-0 text-white">
                              Billed annually • Cancel anytime
                            </p>
                          </div>
                          <div className={styles.savingsBadge}>
                            <i className="bi bi-lightning-charge-fill me-1"></i>
                            Just $100/month
                          </div>
                        </div>
                      </div>
                    </div>

                    {submitMessage && (
                      <div
                        className={`alert ${
                          submitMessage.type === "success"
                            ? styles.alertSuccess
                            : styles.alertDanger
                        } alert-dismissible fade show`}
                        role="alert"
                      >
                        {submitMessage.text}
                        {submitMessage.type !== "success" && (
                          <div className="mt-2 small">
                            If this error continues, contact{" "}
                            <a href="mailto:support@dnc.com">support@dnc.com</a>
                          </div>
                        )}
                        <button
                          type="button"
                          className="btn-close"
                          aria-label="Close"
                          onClick={() => setSubmitMessage(null)}
                        ></button>
                      </div>
                    )}

                    <form onSubmit={handleSubmit(onSubmit)}>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="firstName"
                            className={`form-label ${styles.formLabel}`}
                          >
                            First Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.firstName ? "is-invalid" : ""
                            }`}
                            id="firstName"
                            {...register("firstName")}
                            disabled={isSubmitting}
                          />
                          {errors.firstName && (
                            <div className="invalid-feedback">
                              {errors.firstName.message}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="lastName"
                            className={`form-label ${styles.formLabel}`}
                          >
                            Last Name <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.lastName ? "is-invalid" : ""
                            }`}
                            id="lastName"
                            {...register("lastName")}
                            disabled={isSubmitting}
                          />
                          {errors.lastName && (
                            <div className="invalid-feedback">
                              {errors.lastName.message}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="mb-3">
                        <label
                          htmlFor="email"
                          className={`form-label ${styles.formLabel}`}
                        >
                          Email Address <span className="text-danger">*</span>
                        </label>
                        <input
                          type="email"
                          className={`form-control ${
                            errors.email ? "is-invalid" : ""
                          }`}
                          id="email"
                          {...register("email")}
                          disabled={isSubmitting}
                        />
                        {errors.email && (
                          <div className="invalid-feedback">
                            {errors.email.message}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label
                          htmlFor="phoneNumber"
                          className={`form-label ${styles.formLabel}`}
                        >
                          Phone Number <span className="text-danger">*</span>
                        </label>
                        <input
                          type="tel"
                          className={`form-control ${
                            errors.phoneNumber ? "is-invalid" : ""
                          }`}
                          id="phoneNumber"
                          placeholder="(555) 123-4567"
                          {...register("phoneNumber")}
                          disabled={isSubmitting}
                        />
                        {errors.phoneNumber && (
                          <div className="invalid-feedback">
                            {errors.phoneNumber.message}
                          </div>
                        )}
                      </div>

                      <div className="mb-3">
                        <label
                          htmlFor="company"
                          className={`form-label ${styles.formLabel}`}
                        >
                          Company Name
                        </label>
                        <input
                          type="text"
                          className="form-control"
                          id="company"
                          {...register("company")}
                          disabled={isSubmitting}
                        />
                      </div>

                      {/* Street Address Section */}
                      <div className={`mb-4 ${styles.addressSection}`}>
                        <h5 className={`mb-3 ${styles.sectionHeader}`}>
                          <i className="bi bi-geo-alt me-2"></i>
                          Street Address
                        </h5>

                        <div className="mb-3">
                          <label
                            htmlFor="address"
                            className={`form-label ${styles.formLabel}`}
                          >
                            Street Address{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${
                              errors.address ? "is-invalid" : ""
                            }`}
                            id="address"
                            {...register("address")}
                            disabled={isSubmitting}
                          />
                          {errors.address && (
                            <div className="invalid-feedback">
                              {errors.address.message}
                            </div>
                          )}
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="city"
                              className={`form-label ${styles.formLabel}`}
                            >
                              City <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.city ? "is-invalid" : ""
                              }`}
                              id="city"
                              {...register("city")}
                              disabled={isSubmitting}
                            />
                            {errors.city && (
                              <div className="invalid-feedback">
                                {errors.city.message}
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="state"
                              className={`form-label ${styles.formLabel}`}
                            >
                              State <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.state ? "is-invalid" : ""
                              }`}
                              id="state"
                              {...register("state")}
                              disabled={isSubmitting}
                            />
                            {errors.state && (
                              <div className="invalid-feedback">
                                {errors.state.message}
                              </div>
                            )}
                          </div>
                        </div>

                        <div className="row">
                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="postalCode"
                              className={`form-label ${styles.formLabel}`}
                            >
                              Postal Code <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${
                                errors.postalCode ? "is-invalid" : ""
                              }`}
                              id="postalCode"
                              {...register("postalCode")}
                              disabled={isSubmitting}
                            />
                            {errors.postalCode && (
                              <div className="invalid-feedback">
                                {errors.postalCode.message}
                              </div>
                            )}
                          </div>

                          <div className="col-md-6 mb-3">
                            <label
                              htmlFor="country"
                              className={`form-label ${styles.formLabel}`}
                            >
                              Country <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.country ? "is-invalid" : ""
                              }`}
                              id="country"
                              {...register("country")}
                              disabled={isSubmitting}
                            >
                              <option value="">Select Country</option>
                              <option value="US">United States</option>
                              <option value="Canada">Canada</option>
                            </select>
                            {errors.country && (
                              <div className="invalid-feedback">
                                {errors.country.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Credit Card Section */}
                      <div className={`mb-4 ${styles.creditCardSection}`}>
                        <h5 className={`mb-3 ${styles.sectionHeader}`}>
                          <i className="bi bi-credit-card-2-front me-2"></i>
                          Credit Card Information
                        </h5>

                        <div className="mb-3">
                          <label
                            htmlFor="cardNumber"
                            className={`form-label ${styles.formLabel}`}
                          >
                            Card Number <span className="text-danger">*</span>
                          </label>
                          <input
                            type="text"
                            className={`form-control ${styles.cardNumberInput} ${
                              errors.cardNumber ? "is-invalid" : ""
                            }`}
                            id="cardNumber"
                            placeholder="1234 5678 9012 3456"
                            maxLength={19}
                            {...register("cardNumber")}
                            disabled={isSubmitting}
                            onChange={(e) => {
                              // Format card number with spaces
                              const value = e.target.value
                                .replace(/\s/g, "")
                                .replace(/(.{4})/g, "$1 ")
                                .trim();
                              e.target.value = value;
                              setValue("cardNumber", value);
                            }}
                          />
                          {errors.cardNumber && (
                            <div className="invalid-feedback">
                              {errors.cardNumber.message}
                            </div>
                          )}
                        </div>

                        <div className="row">
                          <div className="col-md-4 mb-3">
                            <label
                              htmlFor="expiryMonth"
                              className={`form-label ${styles.formLabel}`}
                            >
                              Expiry Month{" "}
                              <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.expiryMonth ? "is-invalid" : ""
                              }`}
                              id="expiryMonth"
                              {...register("expiryMonth")}
                              disabled={isSubmitting}
                            >
                              <option value="">Month</option>
                              <option value="01">01 - January</option>
                              <option value="02">02 - February</option>
                              <option value="03">03 - March</option>
                              <option value="04">04 - April</option>
                              <option value="05">05 - May</option>
                              <option value="06">06 - June</option>
                              <option value="07">07 - July</option>
                              <option value="08">08 - August</option>
                              <option value="09">09 - September</option>
                              <option value="10">10 - October</option>
                              <option value="11">11 - November</option>
                              <option value="12">12 - December</option>
                            </select>
                            {errors.expiryMonth && (
                              <div className="invalid-feedback">
                                {errors.expiryMonth.message}
                              </div>
                            )}
                          </div>

                          <div className="col-md-4 mb-3">
                            <label
                              htmlFor="expiryYear"
                              className={`form-label ${styles.formLabel}`}
                            >
                              Expiry Year <span className="text-danger">*</span>
                            </label>
                            <select
                              className={`form-select ${
                                errors.expiryYear ? "is-invalid" : ""
                              }`}
                              id="expiryYear"
                              {...register("expiryYear")}
                              disabled={isSubmitting}
                            >
                              <option value="">Year</option>
                              {Array.from({ length: 15 }, (_, i) => {
                                const year = new Date().getFullYear() + i;
                                return (
                                  <option key={year} value={year.toString()}>
                                    {year}
                                  </option>
                                );
                              })}
                            </select>
                            {errors.expiryYear && (
                              <div className="invalid-feedback">
                                {errors.expiryYear.message}
                              </div>
                            )}
                          </div>

                          <div className="col-md-4 mb-3">
                            <label
                              htmlFor="cvv"
                              className={`form-label ${styles.formLabel}`}
                            >
                              CVV <span className="text-danger">*</span>
                            </label>
                            <input
                              type="text"
                              className={`form-control ${styles.cvvInput} ${
                                errors.cvv ? "is-invalid" : ""
                              }`}
                              id="cvv"
                              placeholder="123"
                              maxLength={4}
                              {...register("cvv")}
                              disabled={isSubmitting}
                              onChange={(e) => {
                                // Only allow digits
                                const value = e.target.value.replace(/\D/g, "");
                                e.target.value = value;
                                setValue("cvv", value);
                              }}
                            />
                            {errors.cvv && (
                              <div className="invalid-feedback">
                                {errors.cvv.message}
                              </div>
                            )}
                          </div>
                        </div>
                      </div>

                      {/* Billing Address Section */}
                      <div className={`mb-4 ${styles.addressSection}`}>
                        <h5 className={`mb-3 ${styles.sectionHeader}`}>
                          <i className="bi bi-credit-card me-2"></i>
                          Billing Address
                        </h5>

                        <div className="mb-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              id="sameAsStreetAddress"
                              checked={sameAsStreetAddress}
                              onChange={(e) => {
                                setSameAsStreetAddress(e.target.checked);
                                if (e.target.checked) {
                                  // Clear billing fields when checkbox is checked
                                  reset({
                                    ...getValues(),
                                    billingAddress: "",
                                    billingCity: "",
                                    billingState: "",
                                    billingPostalCode: "",
                                    billingCountry: undefined,
                                    sameAsStreetAddress: true,
                                  });
                                } else {
                                  // Set sameAsStreetAddress to false when unchecked
                                  setValue("sameAsStreetAddress", false);
                                }
                              }}
                              disabled={isSubmitting}
                            />
                            <label
                              className="form-check-label"
                              htmlFor="sameAsStreetAddress"
                            >
                              Same as street address
                            </label>
                          </div>
                        </div>

                        {!sameAsStreetAddress && (
                          <>
                            <div className="mb-3">
                              <label
                                htmlFor="billingAddress"
                                className={`form-label ${styles.formLabel}`}
                              >
                                Street Address{" "}
                                <span className="text-danger">*</span>
                              </label>
                              <input
                                type="text"
                                className={`form-control ${
                                  errors.billingAddress ? "is-invalid" : ""
                                }`}
                                id="billingAddress"
                                {...register("billingAddress")}
                                disabled={isSubmitting}
                              />
                              {errors.billingAddress && (
                                <div className="invalid-feedback">
                                  {errors.billingAddress.message}
                                </div>
                              )}
                            </div>

                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="billingCity"
                                  className={`form-label ${styles.formLabel}`}
                                >
                                  City <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control ${
                                    errors.billingCity ? "is-invalid" : ""
                                  }`}
                                  id="billingCity"
                                  {...register("billingCity")}
                                  disabled={isSubmitting}
                                />
                                {errors.billingCity && (
                                  <div className="invalid-feedback">
                                    {errors.billingCity.message}
                                  </div>
                                )}
                              </div>

                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="billingState"
                                  className={`form-label ${styles.formLabel}`}
                                >
                                  State <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control ${
                                    errors.billingState ? "is-invalid" : ""
                                  }`}
                                  id="billingState"
                                  {...register("billingState")}
                                  disabled={isSubmitting}
                                />
                                {errors.billingState && (
                                  <div className="invalid-feedback">
                                    {errors.billingState.message}
                                  </div>
                                )}
                              </div>
                            </div>

                            <div className="row">
                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="billingPostalCode"
                                  className={`form-label ${styles.formLabel}`}
                                >
                                  Postal Code{" "}
                                  <span className="text-danger">*</span>
                                </label>
                                <input
                                  type="text"
                                  className={`form-control ${
                                    errors.billingPostalCode ? "is-invalid" : ""
                                  }`}
                                  id="billingPostalCode"
                                  {...register("billingPostalCode")}
                                  disabled={isSubmitting}
                                />
                                {errors.billingPostalCode && (
                                  <div className="invalid-feedback">
                                    {errors.billingPostalCode.message}
                                  </div>
                                )}
                              </div>

                              <div className="col-md-6 mb-3">
                                <label
                                  htmlFor="billingCountry"
                                  className={`form-label ${styles.formLabel}`}
                                >
                                  Country <span className="text-danger">*</span>
                                </label>
                                <select
                                  className={`form-select ${
                                    errors.billingCountry ? "is-invalid" : ""
                                  }`}
                                  id="billingCountry"
                                  {...register("billingCountry")}
                                  disabled={isSubmitting}
                                >
                                  <option value="">Select Country</option>
                                  <option value="US">United States</option>
                                  <option value="Canada">Canada</option>
                                </select>
                                {errors.billingCountry && (
                                  <div className="invalid-feedback">
                                    {errors.billingCountry.message}
                                  </div>
                                )}
                              </div>
                            </div>
                          </>
                        )}
                      </div>

                      <div className="mb-3">
                        <label
                          htmlFor="username"
                          className={`form-label ${styles.formLabel}`}
                        >
                          Username <span className="text-danger">*</span>
                        </label>
                        <input
                          type="text"
                          className={`form-control ${
                            errors.username ? "is-invalid" : ""
                          }`}
                          id="username"
                          {...register("username")}
                          disabled={isSubmitting}
                        />
                        {errors.username && (
                          <div className="invalid-feedback">
                            {errors.username.message}
                          </div>
                        )}
                      </div>

                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="password"
                            className={`form-label ${styles.formLabel}`}
                          >
                            Password <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.password ? "is-invalid" : ""
                            }`}
                            id="password"
                            {...register("password")}
                            disabled={isSubmitting}
                          />
                          <div className="form-text">
                            Password must be at least 8 characters long and
                            contain at least one uppercase letter, one lowercase
                            letter, one digit, and one special character.
                          </div>
                          {errors.password && (
                            <div className="invalid-feedback">
                              {errors.password.message}
                            </div>
                          )}
                        </div>

                        <div className="col-md-6 mb-3">
                          <label
                            htmlFor="confirmPassword"
                            className={`form-label ${styles.formLabel}`}
                          >
                            Confirm Password{" "}
                            <span className="text-danger">*</span>
                          </label>
                          <input
                            type="password"
                            className={`form-control ${
                              errors.confirmPassword ? "is-invalid" : ""
                            }`}
                            id="confirmPassword"
                            {...register("confirmPassword")}
                            disabled={isSubmitting}
                          />
                          {errors.confirmPassword && (
                            <div className="invalid-feedback">
                              {errors.confirmPassword.message}
                            </div>
                          )}
                        </div>
                      </div>

                      {/* Agreement Section */}
                      <div className={`${styles.agreementSection} mb-4`}>
                        {/* Services Agreement */}
                        <div className="form-check mb-3">
                          <input
                            className={`form-check-input ${styles.agreementCheckbox} ${
                              errors.agreeToTerms ? "is-invalid" : ""
                            }`}
                            type="checkbox"
                            id="agreeToTerms"
                            {...register("agreeToTerms")}
                            disabled={isSubmitting}
                          />
                          <label
                            className={`form-check-label ${styles.agreementLabel}`}
                            htmlFor="agreeToTerms"
                          >
                            I have read and agree to be bound by the{" "}
                            <a
                              href="https://www.dnc.com/services-agreement"
                              target="_blank"
                              rel="noopener noreferrer"
                              className={styles.legalLink}
                            >
                              Contact Center Compliance Services Agreement
                            </a>
                            , which governs my access to and use of the
                            Compliance Guide and related services.
                          </label>
                          {errors.agreeToTerms && (
                            <div className="invalid-feedback d-block">
                              {errors.agreeToTerms.message}
                            </div>
                          )}
                        </div>

                        {/* Payment Authorization */}
                        <div className="form-check">
                          <input
                            className={`form-check-input ${styles.agreementCheckbox} ${
                              errors.agreeToCharges ? "is-invalid" : ""
                            }`}
                            type="checkbox"
                            id="agreeToCharges"
                            {...register("agreeToCharges")}
                            disabled={isSubmitting}
                          />
                          <label
                            className={`form-check-label ${styles.agreementLabel}`}
                            htmlFor="agreeToCharges"
                          >
                            <strong>
                              I authorize Contact Center Compliance to charge my
                              credit card $1,200 annually
                            </strong>{" "}
                            for access to the Compliance Guide, starting today.
                            I understand this subscription will automatically
                            renew each year on the anniversary of my signup date
                            unless I cancel. I can cancel my subscription at any
                            time by contacting customer support.
                          </label>
                          {errors.agreeToCharges && (
                            <div className="invalid-feedback d-block">
                              {errors.agreeToCharges.message}
                            </div>
                          )}
                        </div>
                        <div className={`${styles.agreementDetails} mt-2`}>
                          <p className="small text-muted mb-1">
                            <i className="bi bi-shield-check me-1"></i>
                            Your payment information is encrypted and secure
                          </p>
                          <p className="small text-muted mb-0">
                            <i className="bi bi-calendar-check me-1"></i>
                            Annual renewal date:{" "}
                            {new Date(
                              new Date().setFullYear(
                                new Date().getFullYear() + 1,
                              ),
                            ).toLocaleDateString("en-US", {
                              month: "long",
                              day: "numeric",
                              year: "numeric",
                            })}
                          </p>
                        </div>
                      </div>

                      {/* Error message near submit button for visibility */}
                      {submitMessage && submitMessage.type === "error" && (
                        <div
                          className={`alert ${styles.alertDanger} mb-3`}
                          role="alert"
                        >
                          <i className="bi bi-exclamation-triangle-fill me-2"></i>
                          {submitMessage.text}
                          <div className="mt-2 small">
                            If this error continues, contact{" "}
                            <a href="mailto:support@dnc.com">support@dnc.com</a>
                          </div>
                        </div>
                      )}

                      <div className="d-grid gap-2 d-md-flex justify-content-md-end">
                        {/* <button
                          type="button"
                          className={`btn btn-outline-secondary me-md-2 ${styles.btnOutlineSecondary}`}
                          onClick={() => navigate("/compliance-guide/intro")}
                          disabled={isSubmitting}
                        >
                          <i className="bi bi-arrow-left me-1"></i>
                          Back to Guide
                        </button> */}
                        <button
                          type="submit"
                          className={`btn btn-primary btn-lg ${styles.btnPrimary}`}
                          disabled={isSubmitting}
                        >
                          {isSubmitting ? (
                            <>
                              <span
                                className={`spinner-border spinner-border-sm me-2 ${styles.spinnerBorder}`}
                                role="status"
                                aria-hidden="true"
                              ></span>
                              Processing Payment...
                            </>
                          ) : (
                            <>
                              <i className="bi bi-lock-fill me-2"></i>
                              Complete Secure Checkout
                            </>
                          )}
                        </button>
                      </div>
                    </form>
                  </>
                )}
              </div>
            </div>

            {/* {!isSignupSuccess && (
              <div className="mt-4 text-center">
                <p className={`${styles.textMuted} ${styles.small}`}>
                  Already have an account?{" "}
                  <a
                    href="/login"
                    className={styles.textDecorationNone}
                    onClick={(e) => {
                      e.preventDefault();
                      navigate("/login");
                    }}
                  >
                    Sign in here
                  </a>
                </p>
              </div>
            )} */}
          </div>
        </div>
      </div>
    </>
  );
};

export default ComplianceGuideSignup;
